import clsx from "clsx";
import React from "react";
import { DialogProps, Button, useTheme, Typography, Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { humanizeUserPlan } from "../../../utils/user";

import { useCurrentUser } from "../../../hooks/useCurrentUser";

import { BaseDialog } from "../BaseDialog";
import { COACH_PLAN_ROUTE } from "../../../routes/routes";
import { useNavigate } from "react-router-dom";
import { Celebration } from "../../../icons/Celebration";

const useStyles = makeStyles((theme) => ({
  root: {
    textAlign: "center",
  },

  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  svg: {
    width: "100%",
    maxWidth: 345,
    margin: theme.spacing(4, 0),
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
    animation: "$fadeInScale 0.6s ease-out",
  },

  "@keyframes fadeInScale": {
    "0%": {
      opacity: 0,
      transform: "scale(0.8)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },

  button: {
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    color: theme.palette.common.white,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    animation: "$fadeInUp 0.5s ease-out 0.3s both",
  },

  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(10px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  subtitle: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textAlign: "center",
    marginBottom: theme.spacing(1),
    animation: "$fadeIn 0.5s ease-out 0.1s both",
  },

  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
    },
    "100%": {
      opacity: 1,
    },
  },

  planButton: {
    marginTop: theme.spacing(2),
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    padding: theme.spacing(1.5),
    animation: "$fadeInUp 0.5s ease-out 0.4s both",
  },

  titleWrapper: {
    animation: "$fadeIn 0.4s ease-out",
  },
}));

export interface UpgradedDialogProps extends DialogProps {
  plan?: string;
  title?: string;
  description?: string;
  navigateToPlanPage?: boolean;
  showPlanButton?: boolean;
}

export function UpgradedDialog(props: UpgradedDialogProps) {
  const theme = useTheme();

  const {
    className,
    onClose,
    plan,
    title,
    description,
    navigateToPlanPage = true,
    showPlanButton = false,
    ...other
  } = props;

  const navigate = useNavigate();
  const s = useStyles();

  const handleButtonClick = React.useCallback(
    (event) => {
      onClose(event, "backdropClick");
      if (navigateToPlanPage) {
        navigate(COACH_PLAN_ROUTE, { replace: true });
      }
    },
    [onClose, navigateToPlanPage, navigate],
  );

  const handleGoToPlans = React.useCallback(
    (event) => {
      onClose(event, "backdropClick");
      navigate(COACH_PLAN_ROUTE);
    },
    [onClose, navigate],
  );

  const defaultTitle = React.useMemo(() => {
    if (plan) {
      return `Your subscription has been updated to the ${humanizeUserPlan(
        plan,
      )} plan`;
    }
    return "Your subscription has been updated";
  }, [plan]);

  return (
    <BaseDialog
      className={clsx(s.root, className)}
      title={title || defaultTitle}
      showClose={false}
      classNameTitle={s.titleWrapper}
      {...other}
    >
      <Box className={s.content}>
        {description && (
          <Typography variant="body1" className={s.subtitle}>
            {description}
          </Typography>
        )}
        <Celebration className={s.svg} fill={theme.palette.primary.main} />
        <Button
          className={s.button}
          fullWidth
          variant="contained"
          onClick={handleButtonClick}
        >
          OK, Let's Go
        </Button>
        {showPlanButton && (
          <Button
            className={s.planButton}
            fullWidth
            variant="outlined"
            onClick={handleGoToPlans}
          >
            Navigate to Plans
          </Button>
        )}
      </Box>
    </BaseDialog>
  );
}
